export const PROPERTY_PAGE_ALERT_STORAGE_PREFIX = "everhost_propertyPageAlert";

/** Used when settings.propertyPageAlerts is undefined (not yet configured). */
export const DEFAULT_PROPERTY_PAGE_ALERTS = [
  {
    id: "printable-guides",
    title: "New: printable property guides",
    body:
      "Download a print-ready PDF or editable Word (.docx) guide for any property using the printer icon in grid or list view.",
    icon: "mdi-printer-outline",
    type: "info",
    startAt: "",
    endAt: "2026-09-01",
    maxViews: 5,
    enabled: true,
  },
];

export const PROPERTY_PAGE_ALERT_TYPES = [
  { value: "info", label: "Info" },
  { value: "success", label: "Success" },
  { value: "warning", label: "Warning" },
  { value: "error", label: "Error" },
];

export function emptyPropertyPageAlert() {
  return {
    id: "",
    title: "",
    body: "",
    icon: "mdi-information-outline",
    type: "info",
    startAt: "",
    endAt: "",
    maxViews: 5,
    enabled: true,
  };
}

export function normalizePropertyPageAlert(raw = {}) {
  const maxViewsRaw = Number(raw.maxViews);
  return {
    id: String(raw.id || "").trim(),
    title: String(raw.title || "").trim(),
    body: String(raw.body || "").trim(),
    icon: String(raw.icon || "mdi-information-outline").trim() || "mdi-information-outline",
    type: ["info", "success", "warning", "error"].includes(raw.type)
      ? raw.type
      : "info",
    startAt: String(raw.startAt || "").trim(),
    endAt: String(raw.endAt || "").trim(),
    maxViews: Number.isFinite(maxViewsRaw) && maxViewsRaw >= 0 ? maxViewsRaw : 5,
    enabled: raw.enabled !== false,
  };
}

/**
 * Resolve the alert list from settings.
 * - undefined / missing key → fallback (legacy hard-coded alert)
 * - empty array → no alerts (admin cleared them)
 */
export function resolvePropertyPageAlerts(settings) {
  if (!settings || !Object.prototype.hasOwnProperty.call(settings, "propertyPageAlerts")) {
    return DEFAULT_PROPERTY_PAGE_ALERTS.map(normalizePropertyPageAlert);
  }
  const list = settings.propertyPageAlerts;
  if (!Array.isArray(list)) {
    return DEFAULT_PROPERTY_PAGE_ALERTS.map(normalizePropertyPageAlert);
  }
  return list.map(normalizePropertyPageAlert).filter((alert) => alert.id);
}

function todayYmdLocal() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Inclusive startAt, inclusive endAt (alert shows through endAt day). */
export function isPropertyPageAlertInDateWindow(alert, today = todayYmdLocal()) {
  const startAt = String(alert?.startAt || "").trim();
  const endAt = String(alert?.endAt || "").trim();
  if (startAt && today < startAt) {
    return false;
  }
  if (endAt && today > endAt) {
    return false;
  }
  return true;
}

export function propertyPageAlertStorageKey(alertId, uid) {
  if (!alertId || !uid) {
    return null;
  }
  return `${PROPERTY_PAGE_ALERT_STORAGE_PREFIX}_${alertId}_${uid}`;
}

export function propertyPageAlertSessionKey(alertId, uid) {
  if (!alertId || !uid) {
    return null;
  }
  return `${PROPERTY_PAGE_ALERT_STORAGE_PREFIX}_session_${alertId}_${uid}`;
}

export function readPropertyPageAlertState(alertId, uid) {
  const key = propertyPageAlertStorageKey(alertId, uid);
  if (!key) {
    return { visitCount: 0, dismissed: false };
  }
  try {
    const stored = localStorage.getItem(key);
    if (!stored) {
      return { visitCount: 0, dismissed: false };
    }
    const parsed = JSON.parse(stored);
    const visitCount = Number(parsed?.visitCount);
    return {
      visitCount: Number.isFinite(visitCount) ? visitCount : 0,
      dismissed: parsed?.dismissed === true,
    };
  } catch (error) {
    return { visitCount: 0, dismissed: false };
  }
}

export function writePropertyPageAlertState(alertId, uid, state) {
  const key = propertyPageAlertStorageKey(alertId, uid);
  if (!key) {
    return;
  }
  localStorage.setItem(
    key,
    JSON.stringify({
      visitCount: Number(state?.visitCount) || 0,
      dismissed: state?.dismissed === true,
    }),
  );
}

/** Increment visitCount at most once per browser tab session per alert. */
export function recordPropertyPageAlertView(alertId, uid) {
  const sessionKey = propertyPageAlertSessionKey(alertId, uid);
  if (!sessionKey) {
    return readPropertyPageAlertState(alertId, uid);
  }

  try {
    if (sessionStorage.getItem(sessionKey)) {
      return readPropertyPageAlertState(alertId, uid);
    }
    // Claim the session slot before reading/writing localStorage so concurrent
    // syncs (mounted + watchers) cannot double-count.
    sessionStorage.setItem(sessionKey, "1");
  } catch (error) {
    // sessionStorage unavailable — fall through and count once.
  }

  const state = readPropertyPageAlertState(alertId, uid);
  if (state.dismissed) {
    return state;
  }
  const next = {
    visitCount: state.visitCount + 1,
    dismissed: false,
  };
  writePropertyPageAlertState(alertId, uid, next);
  return next;
}

export function shouldShowPropertyPageAlert(alert, uid) {
  const normalized = normalizePropertyPageAlert(alert);
  if (!normalized.enabled || !normalized.id || !uid) {
    return false;
  }
  if (!isPropertyPageAlertInDateWindow(normalized)) {
    return false;
  }
  const state = readPropertyPageAlertState(normalized.id, uid);
  if (state.dismissed) {
    return false;
  }
  if (normalized.maxViews > 0 && state.visitCount >= normalized.maxViews) {
    return false;
  }
  return true;
}

export function validatePropertyPageAlerts(alerts) {
  const list = Array.isArray(alerts) ? alerts : [];
  const ids = new Set();
  for (const raw of list) {
    const alert = normalizePropertyPageAlert(raw);
    if (!alert.id) {
      return "Each alert needs an ID.";
    }
    if (!/^[a-zA-Z0-9_-]{1,64}$/.test(alert.id)) {
      return `Alert ID "${alert.id}" must be 1–64 characters: letters, numbers, underscore, or hyphen.`;
    }
    if (ids.has(alert.id)) {
      return `Duplicate alert ID "${alert.id}".`;
    }
    ids.add(alert.id);
    if (!alert.title) {
      return `Alert "${alert.id}" needs a title.`;
    }
    if (
      alert.startAt &&
      alert.endAt &&
      alert.endAt < alert.startAt
    ) {
      return `Alert "${alert.id}" end date must be on or after the start date.`;
    }
    if (alert.maxViews < 0) {
      return `Alert "${alert.id}" max views cannot be negative.`;
    }
  }
  return null;
}
